import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAuthToken } from '../utils/auth';
import { threediumApi } from '../services/cloudApi';

function Header({ 
  activeMode, 
  onModeChange, 
  showLeftRibbon = false, 
  showBackButton = false,
  onBackToDashboard,
  pageTitle,
  theme = 'dark',
  onThemeChange,
  chatSidebarOpen = true,
  onToggleChatSidebar,
  viewMode = 'preview',
  onViewModeChange,
  deviceMode = 'desktop',
  onDeviceModeChange,
  minimalMode = false,
  onPublishClick,
  project = null
}) {
  const activeDevice = deviceMode;
  const setActiveDevice = onDeviceModeChange || (() => {});
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [credits, setCredits] = useState(null);
  const isDarkMode = theme === 'dark';
  
  const { user, isAuthenticated, cloudAvailable, logout, isAdmin, sessionExpiringSoon } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      setCredits(null);
      return;
    }
    let cancelled = false;
    threediumApi.getCredits()
      .then(data => {
        if (!cancelled) setCredits(Math.floor((data?.total ?? 0) / 100));
      })
      .catch(err => {
        console.error('Failed to fetch Threedium credits (endpoint may not be available on this environment):', err);
        if (!cancelled) setCredits(0);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated]);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  const handleThemeToggle = (newTheme) => {
    if (onThemeChange) {
      onThemeChange(newTheme);
    }
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadZip = async () => {
    if (!project?.aws_project_id && !project?.id) {
      console.warn('No project available for download');
      return;
    }
    
    const projectId = project.aws_project_id || project.id;
    setIsDownloading(true);
    
    try {
      const token = getAuthToken();
      const cloudApiUrl = process.env.REACT_APP_CLOUD_API_URL || 'http://localhost:8080';
      
      const response = await fetch(`${cloudApiUrl}/api/projects/${projectId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
      }
      
      // Get the filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'project.zip';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      
      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Failed to download project:', error);
      console.warn('Failed to download project. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <header className="builder-header">
      <div className="header-content">
        {/* Left Section - Logo, Dashboard, and Builder Controls */}
        <div className="header-left">
          {/* Logo + Dashboard Group */}
          <div className="header-logo-nav">
            {/* Logo */}
            <div className="header-logo">
              <img src="/assets/header/logo.svg" alt="Threedium" />
            </div>
            
            {/* Dashboard Button - Visible when in workspace mode OR on Layout-wrapped pages */}
            {(showLeftRibbon || showBackButton) && (
              <button 
                className="header-nav-btn" 
                onClick={() => {
                  if (onBackToDashboard) {
                    onBackToDashboard();
                  } else {
                    navigate('/dashboard');
                  }
                }}
              >
                <BackIcon />
                <span>Dashboard</span>
              </button>
            )}

            {/* Page Title - Show on Layout-wrapped pages */}
            {pageTitle && !showLeftRibbon && (
              <>
                <div className="header-separator" />
                <span className="header-page-title">{pageTitle}</span>
              </>
            )}
          </div>

          {/* Left Ribbon - Contains all icon groups */}
          <div className="header-left-ribbon">
            {/* Chat Sidebar Toggle - Only visible after first prompt, hidden in minimal mode */}
            {showLeftRibbon && !minimalMode && (
              <>
                <button 
                  className={`header-sidebar-toggle ${chatSidebarOpen ? 'active' : ''}`}
                  onClick={onToggleChatSidebar}
                  title={chatSidebarOpen ? 'Hide Chat Sidebar' : 'Show Chat Sidebar'}
                >
                  <SidebarToggleIcon isOpen={chatSidebarOpen} />
                </button>

                <div className="header-separator" />

                {/* Device Toggle */}
                <div className="header-device-toggle">
                  <button 
                    className={`header-icon-btn ${activeDevice === 'mobile' ? 'active' : ''}`}
                    onClick={() => setActiveDevice('mobile')}
                  >
                    <img src="/assets/header/mobile.svg" alt="Mobile" />
                  </button>
                  <button 
                    className={`header-icon-btn ${activeDevice === 'tablet' ? 'active' : ''}`}
                    onClick={() => setActiveDevice('tablet')}
                  >
                    <img src="/assets/header/tablet.svg" alt="Tablet" />
                  </button>
                  <button 
                    className={`header-icon-btn ${activeDevice === 'desktop' ? 'active' : ''}`}
                    onClick={() => setActiveDevice('desktop')}
                  >
                    <img src="/assets/header/desktop.svg" alt="Desktop" />
                  </button>
                </div>

                <div className="header-separator" />
              </>
            )}

            {/* Theme Toggle - Always visible */}
            <div className="header-theme-toggle">
              <button 
                className={`header-icon-btn ${!isDarkMode ? 'active' : ''}`}
                onClick={() => handleThemeToggle('light')}
              >
                <img src="/assets/header/light.svg" alt="Light Mode" />
              </button>
              <button 
                className={`header-icon-btn ${isDarkMode ? 'active' : ''}`}
                onClick={() => handleThemeToggle('dark')}
              >
                <img src="/assets/header/dark.svg" alt="Dark Mode" />
              </button>
            </div>

            {/* Preview/Code - Only visible in Builder mode workspace, hidden in minimal mode */}
            {showLeftRibbon && activeMode === 'builder' && !minimalMode && (
              <>
                <div className="header-separator" />

                <div className="header-preview-code">
                  <button 
                    className={`header-text-btn ${viewMode === 'preview' ? 'active' : ''}`}
                    onClick={() => onViewModeChange?.('preview')}
                  >
                    <img src="/assets/header/preview.svg" alt="Preview" />
                    <span>Preview</span>
                  </button>
                  <button 
                    className={`header-text-btn ${viewMode === 'code' ? 'active' : ''}`}
                    onClick={() => onViewModeChange?.('code')}
                  >
                    <img src="/assets/header/code.svg" alt="Code" />
                    <span>Code</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Center Section - Mode Switcher (only visible in chat mode, hidden in minimal mode) */}
        <div className="header-center">
          {showLeftRibbon && !minimalMode && (
            <div className="mode-switcher">
              <button
                className={`mode-btn ${activeMode === 'builder' ? 'active' : ''}`}
                onClick={() => onModeChange('builder')}
              >
                <BuilderIcon />
                <span>Builder</span>
              </button>
              <button
                className={`mode-btn ${activeMode === 'agent-designer' ? 'active' : ''}`}
                onClick={() => onModeChange('agent-designer')}
              >
                <AgentDesignerIcon />
                <span>Designer</span>
              </button>
            </div>
          )}
        </div>

        {/* Right Section - Always visible */}
        <div className="header-right">
          {/* Credits Badge */}
          <div className="header-credits-badge">
            <div className="credits-section">
              <img src="/assets/header/credits.svg" alt="Credits" className="credits-icon" />
              <span className="credits-amount">{credits != null ? credits.toLocaleString() : '...'}</span>
            </div>
            <div className="credits-divider" />
            <div className="upgrade-section">
              <img src="/assets/header/upgrade.svg" alt="Upgrade" className="upgrade-icon" />
              <span className="upgrade-text">Upgrade</span>
            </div>
          </div>

          {/* Settings - hidden in minimal mode */}
          {!minimalMode && (
          <button className="header-icon-btn-dark">
            <img src="/assets/header/settings.svg" alt="Settings" />
          </button>
          )}

          {/* Download ZIP - Only in builder workspace with active project, hidden in minimal mode */}
          {showLeftRibbon && !minimalMode && project && (
            <button 
              className="header-copy-btn" 
              onClick={handleDownloadZip}
              disabled={isDownloading}
              title="Download project source code as ZIP"
            >
              <span>{isDownloading ? 'Downloading...' : 'Download Code'}</span>
            </button>
          )}

          {/* Publish Button - Only in builder workspace, hidden in minimal mode */}
          {showLeftRibbon && !minimalMode && (
            <button className="header-publish-btn" onClick={onPublishClick}>
              <span>Publish</span>
            </button>
          )}

          {/* Divider */}
          <div className="header-divider-vertical" />

          {/* Cloud Status Indicator */}
          <div className={`header-cloud-status ${cloudAvailable ? 'connected' : 'disconnected'}`}>
            <span className="cloud-dot"></span>
          </div>

          {/* Session expiring soon warning */}
          {sessionExpiringSoon && isAuthenticated && (
            <div className="header-session-warning" title="Your session is about to expire. Consider signing in again.">
              <span className="session-warning-icon">⏱</span>
              <span>Session expiring soon</span>
            </div>
          )}

          {/* Profile Avatar with User Menu */}
          {isAuthenticated ? (
            <div className="header-profile-container">
              <button 
                className="header-profile-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="profile-avatar">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="" />
                  ) : (
                    <span>{(user?.display_name || user?.email || 'U')[0].toUpperCase()}</span>
                  )}
                </div>
                <div className="profile-status-dot" />
              </button>

              {showUserMenu && (
                <>
                  <div className="header-menu-backdrop" onClick={() => setShowUserMenu(false)} />
                  <div className="header-user-menu">
                    {/* User Profile Section */}
                    <div className="header-menu-section">
                      <div className="header-menu-profile">
                        <div className="header-menu-avatar">
                          {user?.avatar_url ? (
                            <img src={user.avatar_url} alt="" />
                          ) : (
                            <span>{(user?.display_name || user?.email || 'U')[0].toUpperCase()}</span>
                          )}
                        </div>
                        <div className="header-menu-user-info">
                          <span className="header-menu-name">{user?.display_name || 'User'}</span>
                          <span className="header-menu-email">{user?.email}</span>
                        </div>
                      </div>
                    </div>

                    {/* Tier Badge Section */}
                    <div className="header-menu-section header-menu-tier-section">
                      <span className={`header-menu-tier-badge ${user?.tier?.toLowerCase()}`}>
                        {user?.tier}
                      </span>
                    </div>

                    {/* Dashboard - Go to Builder */}
                    <div className="header-menu-section">
                      <Link to="/dashboard" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                        <DashboardIcon />
                        <span>Dashboard</span>
                      </Link>
                    </div>

                    {/* Agent Designer */}
                    <div className="header-menu-section">
                      <Link to="/designer" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                        <AgentDesignerIcon />
                        <span>Agent Designer</span>
                      </Link>
                    </div>

                    {/* Profile & Settings */}
                    <div className="header-menu-section">
                      <Link to="/settings" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                        <SettingsIcon />
                        <span>Profile & Settings</span>
                      </Link>
                    </div>

                    {/* My Projects */}
                    <div className="header-menu-section">
                      <Link to="/dashboard" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                        <FolderIcon />
                        <span>My Projects</span>
                      </Link>
                    </div>

                    {/* Usage Analytics */}
                    <div className="header-menu-section">
                      <Link to="/usage" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2 14V8M6 14V4M10 14V6M14 14V2"/></svg>
                        <span>Usage Analytics</span>
                      </Link>
                    </div>

                    {/* Admin Section */}
                    {isAdmin && (
                      <>
                        <div className="header-menu-section">
                          <Link to="/admin" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                            <AdminIcon />
                            <span>Admin Panel</span>
                          </Link>
                        </div>
                        <div className="header-menu-section">
                          <Link to="/admin/users" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                            <UsersIcon />
                            <span>User Management</span>
                          </Link>
                        </div>
                        <div className="header-menu-section">
                          <Link to="/admin/tier-limits" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                            <SettingsIcon />
                            <span>Tier Limits</span>
                          </Link>
                        </div>
                        <div className="header-menu-section">
                          <Link to="/admin/models" className="header-menu-item" onClick={() => setShowUserMenu(false)}>
                            <SettingsIcon />
                            <span>LLM Models</span>
                          </Link>
                        </div>
                      </>
                    )}

                    {/* Logout Section */}
                    <div className="header-menu-section header-menu-section-logout">
                      <button className="header-menu-item logout" onClick={handleLogout}>
                        <LogoutIcon />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <Link to="/login" className="header-login-btn">
              Sign In
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

// Icon Components
function BackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BuilderIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function AgentDesignerIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 2V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M8 12V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M2 8H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M12 8H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// Animated sidebar toggle icon - similar to Agent Designer's panel toggles
function SidebarToggleIcon({ isOpen }) {
  return (
    <svg 
      width="16" 
      height="16" 
      viewBox="0 0 16 16" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className="sidebar-toggle-icon"
    >
      {/* Panel outline */}
      <rect 
        x="1.5" 
        y="2.5" 
        width="13" 
        height="11" 
        rx="1.5" 
        stroke="currentColor" 
        strokeWidth="1.5"
      />
      {/* Sidebar divider line */}
      <line 
        x1="5.5" 
        y1="2.5" 
        x2="5.5" 
        y2="13.5" 
        stroke="currentColor" 
        strokeWidth="1.5"
        style={{
          opacity: isOpen ? 1 : 0.5,
          transition: 'opacity 0.2s ease'
        }}
      />
      {/* Arrow indicator */}
      <path 
        d={isOpen ? "M9 8L11.5 6V10L9 8Z" : "M11.5 8L9 6V10L11.5 8Z"}
        fill="currentColor"
        style={{
          transition: 'transform 0.2s ease'
        }}
      />
    </svg>
  );
}

// User menu icons
function SettingsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.93 2.93l1.41 1.41M11.66 11.66l1.41 1.41M2.93 13.07l1.41-1.41M11.66 4.34l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3 13.5c0-2.5 2.5-4 5-4s5 1.5 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 4.5a1.5 1.5 0 011.5-1.5h3l1.5 1.5h5a1.5 1.5 0 011.5 1.5v6a1.5 1.5 0 01-1.5 1.5h-10A1.5 1.5 0 012 12V4.5z" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function AdminIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="6" cy="4" r="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2 12c0-2 2-3 4-3s4 1 4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="11" cy="5" r="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M14 12c0-1.5-1.5-2.5-3-2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 2h6a2 2 0 012 2v8a2 2 0 01-2 2H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M10 8H2m0 0l2.5-2.5M2 8l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default Header;
